import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, getEventById, getEventInfosByEventId, getTicketTypesByEventId, getTicketTypeInfosByTicketTypeId, getTicketTypeNumberOfAvailableTickets } from "ticketino-api-client";
import axios from "axios";

import Header from "./components/Header";

import imgIconAdd from "../images/icons/add.svg";
import imgIconRemove from "../images/icons/remove.svg";
import imgIconDelete from "../images/icons/delete.svg";


const Home = () => {
    const [orderId, setOrderId] = useState();

    const [eventId, setEventId] = useState();
    const [posId, setPosId] = useState();
    const [event, setEvent] = useState({});

    const [ticketTypes, setTicketTypes] = useState([]);
    const [order, setOrder] = useState({ tickets: [], totalPrice: 0, currency: "CHF" });
    const [pendingTicketTypeId, setPendingTicketTypeId] = useState(null);

    const [loading, setLoading] = useState(true);
    const [loaded, setLoaded] = useState(false);

    const [promotioncode, setPromotioncode] = useState("");
    const [promotionError, setPromotionError] = useState({ text: "", valid: false });
    const [addedPromotions, setAddedPromotions] = useState([]);

    // react hook for navigation
    let navigate = useNavigate();

    // base url
    let baseUrl = process.env.REACT_APP_BASEURL_API;

    // setting the base url of the npm package api calls
    api.defaults.baseURL = baseUrl;

    // fetching resources
    const [resources, setResources] = useState({});

    // fetching params
    let { language } = useParams();

    let languageId = 0;

    // changing languageId according to the url
    switch (language) {
        case ("de" || "DE"):
            languageId = 1;
            break;
        case ("en" || "EN"):
            languageId = 3;
            break;
        default:
            languageId = 0;
            language = "de";
            break;
    }

    useEffect(() => {
        loadToken();

        // clear the addedPromocodes in the session
        let array = [];
        sessionStorage.setItem("AddedPromotions", JSON.stringify(array));
        sessionStorage.removeItem("buyerInfo");
    }, []); //only on first page load

    useEffect(() => {
        requestResources();

        // so it wont get called on first load
        if (loaded) {
            startOrder(posId, eventId);
        }
    }, [language]); //everytime language is changed

    useEffect(() => {
        if (orderId) {
            loadEvent(eventId, orderId)
        }
    }, [orderId]); //gets called when an order is started

    // the promotion codes live on the tickets, so an empty basket carries none anymore
    useEffect(() => {
        if ((order.tickets ?? []).length === 0 && addedPromotions.length > 0) {
            setAddedPromotions([]);
            sessionStorage.setItem("AddedPromotions", JSON.stringify([]));

            setPromotionError({ text: "", valid: false });
        }
    }, [order]);

    const loadToken = async () => {
        await axios.get("form/token").then((res) => {
            axios.defaults.headers.common["Authorization"] = "Bearer " + res.data;
            api.defaults.headers.common['Authorization'] = "Bearer " + res.data;

            sessionStorage.setItem("token", res.data);

            requestFormSettings();
        });
    };

    const requestFormSettings = async () => {
        await axios.get(`form/formsettings`).then((res) => {
            // setting the posID and eventID
            setPosId(res.data.posId);
            setEventId(res.data.eventId);

            resumeOrStartOrder(res.data.posId, res.data.eventId);
        });
    };

    // coming back to this page (e.g. from the address step) keeps the basket:
    // the order of the session is reused so the quantities and promotion codes are still shown
    const resumeOrStartOrder = async (posId, eventId) => {
        const existingOrderId = sessionStorage.getItem("orderId");

        if (existingOrderId) {
            const existingOrder = await getOrderByOrderId(existingOrderId);

            if (existingOrder?.tickets?.length > 0) {
                setOrderId(existingOrderId);
                setOrder(existingOrder);

                // the codes applied to the order are the ones to show again
                const codes = [...new Set(existingOrder.tickets.map(ticket => ticket.promotionCode).filter(Boolean))];
                setAddedPromotions(codes);
                sessionStorage.setItem("AddedPromotions", JSON.stringify(codes));

                setLoaded(true);
                return;
            }
        }

        startOrder(posId, eventId);
    };

    const requestResources = async () => {
        await axios
            .get(`form/resources/${language}`)
            .then((res) => {
                setResources(res.data?.translation);
            })
            .catch((error) => console.error(error.response.data));
    };

    const startOrder = async (posId, eventId) => {
        let newOrder = {
            affiliateShopId: null,
            currency: "CHF",
            tenantId: 1,
            languageId: languageId,
            pointOfSaleId: posId,
            abbreviation: "",
            paymentType: 1,
        };

        await axios
            .post(`${baseUrl}/ShopBasket/Order`, newOrder)
            .then((res) => {
                sessionStorage.setItem("orderId", res.data.id);
                setOrderId(res.data.id);
                setOrder({ tickets: [], totalPrice: 0, currency: "CHF" });

                // a new order carries no promotion codes
                setAddedPromotions([]);
                sessionStorage.setItem("AddedPromotions", JSON.stringify([]));

                setLoaded(true);
            })
            .catch((error) => console.error(error.response.data));
    };

    const loadEvent = async (eventId, orderId) => {
        try {
            setLoading(true);

            // Event
            const event = await getEventById(eventId);
            const infos = await getEventInfosByEventId(eventId);

            // sorting it according to the language
            const eventInfo = infos.eventInfos.find(info => info.languageId == languageId);

            // setting the eventInfo object in the info attribute of event
            const updatedEventInfo = { ...event, info: eventInfo };

            // TicketType
            const ticketTypes = await getTicketTypesByEventId(eventId);

            const updatedTicketTypes = await Promise.all(
                ticketTypes.map(async (ticketType) => {
                    const info = await getTicketTypeInfosByTicketTypeId(ticketType.id);

                    // sorting it according to the language
                    const ticketTypeInfo = info.find(info => info.languageId == languageId) ?? info[0];

                    const ticketTypeAvailability = await getTicketTypeNumberOfAvailableTickets(ticketType.id, orderId);

                    // setting the ticketTypeInfo and ticketTypeAvailability object as attributes in ticketType (info and availability)
                    return { ...ticketType, info: ticketTypeInfo, availability: ticketTypeAvailability }
                })
            )

            // setting the values in the end
            setEvent(updatedEventInfo);
            setTicketTypes(updatedTicketTypes);
            setLoading(false);

        } catch (error) {
            console.error(error);
        }
    }

    const getOrderByOrderId = async (orderId) => {
        try {
            const res = await axios.get(`${baseUrl}/ShopBasket/Order/${orderId}`);
            return res.data;
        } catch (error) {
            console.error(error);
        }
    }

    const addPromotionToOrder = async () => {
        try {
            if (promotioncode === "") return;

            await axios.put(`${baseUrl}/ShopBasket/Order/${orderId}/PromotionCode/${promotioncode}`);

            setPromotionError({
                text: `${resources?.PromotionSuccess} (${promotioncode})`,
                valid: true
            });

            const array = [...addedPromotions, promotioncode];
            setAddedPromotions(array);
            sessionStorage.setItem("AddedPromotions", JSON.stringify(array));

            const updatedOrder = await getOrderByOrderId(orderId);
            if (updatedOrder) setOrder(updatedOrder);

            setPromotioncode("");
        } catch (error) {
            setPromotionError({
                text: resources?.PromotionError,
                valid: false
            });
        }
    }

    const removePromotionFromOrder = async (promotion) => {
        try {
            await axios.delete(`${baseUrl}/ShopBasket/Order/${orderId}/PromotionCode/${promotion}`);

            setPromotionError({ text: "", valid: false });

            const array = addedPromotions.filter((promo) => promo !== promotion);
            setAddedPromotions(array);
            sessionStorage.setItem("AddedPromotions", JSON.stringify(array));

            // reset the payment type, the order can be free while a promotion code is applied
            await axios.put(`${baseUrl}/ShopBasket/Order/${orderId}/PaymentType/1`);

            const updatedOrder = await getOrderByOrderId(orderId);
            if (updatedOrder) setOrder(updatedOrder);
        } catch (error) {
            setPromotionError({
                text: resources?.PromotionError,
                valid: false
            });
        }
    }

    const getTicketQuantity = (ticketTypeId) =>
        (order.tickets ?? []).filter((ticket) => ticket.ticketTypeId === ticketTypeId).length;

    const refreshTicketTypeAvailability = async (ticketTypeId) => {
        try {
            const availability = await getTicketTypeNumberOfAvailableTickets(ticketTypeId, orderId);
            setTicketTypes((prev) => prev.map((tt) => tt.id === ticketTypeId ? { ...tt, availability } : tt));
        } catch (error) {
            console.error(error);
        }
    };

    const onSubmit = () => {
        // gtm - addToCart
        //window.dataLayer = window.dataLayer || [];
        //window.dataLayer.push({
        //    event: 'addToCart',
        //});

        // the workshops step is temporarily skipped
        // to bring it back, navigate to `/${language}/workshops` again
        navigate(`/${language}/address`);
    };

    const incrementTicket = async (ticketTypeId, max) => {
        if (getTicketQuantity(ticketTypeId) >= max) return;

        setPendingTicketTypeId(ticketTypeId);
        try {
            const res = await axios.post(`${baseUrl}/ShopBasket/Order/${orderId}/Tickets`, {
                ticketsToAdd: [{ ticketTypeId, quantity: 1 }],
            });

            await refreshTicketTypeAvailability(ticketTypeId);

            // applying a code switches the order to a payment type with fees, which is not used here
            await axios.put(`${baseUrl}/ShopBasket/Order/${orderId}/PaymentType/1`);

            const updatedOrder = await getOrderByOrderId(orderId);
            if (updatedOrder) setOrder(updatedOrder);

        } catch (error) {
            console.error(error.response?.data);
        } finally {
            setPendingTicketTypeId(null);
        }
    };

    const decrementTicket = async (ticketTypeId) => {
        const ticketToRemove = [...(order.tickets ?? [])].reverse().find((ticket) => ticket.ticketTypeId === ticketTypeId);
        if (!ticketToRemove) return;

        setPendingTicketTypeId(ticketTypeId);
        try {
            const res = await axios.delete(`${baseUrl}/ShopBasket/Order/${orderId}/Tickets`, {
                data: { TicketsToRemove: [ticketToRemove.id] },
            });

            setOrder(res.data);
            await refreshTicketTypeAvailability(ticketTypeId);
        } catch (error) {
            console.error(error.response?.data);
        } finally {
            setPendingTicketTypeId(null);
        }
    };

    const mapTicketTypes = () => {
        let ticketTypesSorted = [];

        if (ticketTypes && ticketTypes.length > 0) {
            ticketTypesSorted = [...ticketTypes].sort((a, b) => a.sortOrder - b.sortOrder);
        }

        return (
            ticketTypes &&
            ticketTypes.length > 0 &&
            (() => {
                const visibleTickets = ticketTypesSorted.filter(tt => tt.availability?.status === 0 || tt.availability?.status === 1);

                return (
                    <>
                        {visibleTickets.map((tt, index) => (
                            <div className="row align-items-center mb-4 ticket-row" key={tt.id}>
                                <div className="col-6">
                                    <label className="ticket-name-label">
                                        {tt.info?.name}
                                    </label>
                                </div>

                                <div className="col-3">
                                    <label className="text-nowrap">
                                        {tt.currency} {tt.price.toFixed(2)}
                                    </label>
                                </div>

                                <div className="col-3">
                                    {tt.availability?.availableTickets === 0 ? (
                                        <span className="text-soldout">{resources?.SoldOut}</span>
                                    ) : (
                                        <div className="d-flex align-items-center gap-2">
                                            <button
                                                type="button"
                                                className="btn-counter"
                                                onClick={() => decrementTicket(tt.id)}
                                                disabled={pendingTicketTypeId === tt.id || getTicketQuantity(tt.id) <= 0}
                                            >
                                                <img src={imgIconRemove} alt="Decrease" width="16" height="16" />
                                            </button>

                                            <span className="ticket-quantity">{getTicketQuantity(tt.id)}</span>

                                            <button
                                                type="button"
                                                className="btn-counter"
                                                onClick={() => incrementTicket(tt.id, tt.availability?.availableTickets)}
                                                disabled={pendingTicketTypeId === tt.id || getTicketQuantity(tt.id) >= tt.availability?.availableTickets}
                                            >
                                                <img src={imgIconAdd} alt="Increase" width="16" height="16" />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </>
                );
            })()
        );
    };

    const noTicketsInBasket = (order.tickets ?? []).length === 0;

    // sum of the list prices shown in the ticket rows, before any promotion code
    const grossPrice = ticketTypes.reduce((sum, tt) => sum + tt.price * getTicketQuantity(tt.id), 0);
    const reduction = grossPrice - (order?.totalPrice ?? 0);

    return (
        <>
            <Header language={language} pagePath="home" />
            <div className="page-container">

                <div className="container wrapper">

                    {/* intro */}
                    <div className="row">
                        <div className="col-md-12">
                            <h1 className="text-title">{resources?.HomeTitle}</h1>
                            <div className="mt-4 home-description" dangerouslySetInnerHTML={{ __html: resources?.HomeDescription }}></div>
                        </div>
                    </div>

                    <div className="row mt-5">
                        <div className="col-md-12">
                            {mapTicketTypes()}
                        </div>
                    </div>

                    {reduction > 0.005 && (
                        <div className="row mt-2 ticket-row fw-bold text-pink">
                            <div className="col-6"></div>
                            <div className="col-3">
                                <span>{resources?.Reduction}</span>
                            </div>
                            <div className="col-3">
                                <span className="text-nowrap">
                                    - {order?.currency ?? "CHF"} {reduction.toFixed(2)}
                                </span>
                            </div>
                        </div>
                    )}

                    <div className="row mt-3 ticket-row fw-bold">
                        <div className="col-6"></div>
                        <div className="col-3">
                            <span>{resources?.OverviewTotal}</span>
                        </div>
                        <div className="col-3">
                            <span className="text-nowrap">
                                {order?.currency ?? "CHF"} {(order?.totalPrice ?? 0).toFixed(2)}
                            </span>
                        </div>
                    </div>

                    {/* discount code */}
                    <div className={`mt-5 ${noTicketsInBasket ? "pe-none" : ""}`}>
                        <p className={`mb-2 ${noTicketsInBasket ? "opacity-50" : ""}`}>{resources?.PromotionCode}</p>
                        <p className={noTicketsInBasket ? "opacity-50" : ""} dangerouslySetInnerHTML={{ __html: resources?.PromotionDescription }}></p>
                        <div className="row align-items-end">
                            <div className="col-md-4 col-8 mb-3 mb-md-0">
                                <input className={`form-control ${noTicketsInBasket ? "opacity-50" : ""}`} disabled={noTicketsInBasket} value={promotioncode} onChange={(e) => setPromotioncode(e.target.value)} />
                            </div>
                            <div className="col-md-4">
                                <button className="btn btn-custom" disabled={noTicketsInBasket} onClick={addPromotionToOrder}>{resources?.PromotionRedeem}</button>
                            </div>
                        </div>

                        {promotionError.text &&
                            <div className="row">
                                <div className="col-md-12 mt-2">
                                    {!promotionError.valid ?
                                        <span className="error">{promotionError.text}</span> :
                                        <span className="text-success">{promotionError.text}</span>
                                    }
                                </div>
                            </div>
                        }

                        {/* applied promotion codes */}
                        {[...new Set(addedPromotions)].map((promotion) => (
                            <div className="row mt-3" key={promotion}>
                                <div className="col-md-4 col-8 d-flex align-items-center fw-bold">
                                    <span>{promotion}</span>
                                    <button
                                        type="button"
                                        className="btn-counter cursor-pointer ms-3"
                                        onClick={() => removePromotionFromOrder(promotion)}
                                        aria-label={resources?.PromotionRemove}
                                    >
                                        <img src={imgIconDelete} alt="delete" width="16" height="16" />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="row mt-4">
                        <div className="col-md-12 text-end">
                            <button className="btn btn-custom" disabled={noTicketsInBasket} onClick={() => onSubmit()}>{resources?.Next}</button>
                        </div>
                    </div>

                    <div className="row mt-5">
                        <div className="col-md-12 text-center">
                            <p className="mt-4">
                                <small className="powered-by">powered by <a href="https://organizer.ticketino.com/de" target="_blank">TICKETINO</a></small>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default Home